import { supabase } from '@/integrations/supabase/client';

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;

async function callEdgeFunction(
  functionName: string,
  options: {
    method?: string;
    params?: Record<string, string>;
    body?: any;
  } = {}
) {
  const { method = 'GET', params, body } = options;

  const url = new URL(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`
  );
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') url.searchParams.set(k, v);
    });
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };

  // Add auth token if user is logged in
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Gracefully handle 401 when there is no session (e.g. just after logout)
  // so it never bubbles to the runtime error overlay.
  if (res.status === 401 && !session?.access_token) {
    return { unauthorized: true } as any;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

// ─── Search Engine ─────────────────────────────────────────────
export interface SearchParams {
  q?: string;
  city?: string;
  sub_city?: string;
  woreda?: string;
  listing_type?: string;
  property_type?: string;
  min_price?: string;
  max_price?: string;
  min_bedrooms?: string;
  max_bedrooms?: string;
  min_area?: string;
  max_area?: string;
  is_furnished?: string;
  has_parking?: string;
  is_featured?: string;
  currency?: string;
  sort_by?: string;
  page?: string;
  limit?: string;
}

export interface SearchResult {
  properties: any[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const searchApi = {
  search: (params: SearchParams): Promise<SearchResult> =>
    callEdgeFunction('search-engine', { params: params as Record<string, string> }),
};

// ─── Property Management ───────────────────────────────────────
export const propertyApi = {
  get: (id: string) =>
    callEdgeFunction('property-management', { params: { id } }),

  listMine: (page = 1, limit = 20, status?: string) =>
    callEdgeFunction('property-management', {
      params: { page: String(page), limit: String(limit), ...(status ? { status } : {}) },
    }),

  create: (data: any) =>
    callEdgeFunction('property-management', { method: 'POST', body: data }),

  update: (data: any) =>
    callEdgeFunction('property-management', { method: 'PUT', body: data }),

  delete: (id: string) =>
    callEdgeFunction('property-management', { method: 'DELETE', params: { id } }),
};

// ─── Lead Management ───────────────────────────────────────────
export const leadApi = {
  create: (data: {
    name: string;
    phone: string;
    email?: string;
    message?: string;
    property_id?: string;
    project_id?: string;
    plot_id?: string;
    source?: string;
  }) =>
    callEdgeFunction('lead-management', { method: 'POST', body: data }),

  list: (page = 1, limit = 20, status?: string) =>
    callEdgeFunction('lead-management', {
      params: { page: String(page), limit: String(limit), ...(status ? { status } : {}) },
    }),

  updateStatus: (id: string, status: string) =>
    callEdgeFunction('lead-management', { method: 'PUT', body: { id, status } }),

  addNote: (lead_id: string, note: string) =>
    callEdgeFunction('lead-management', {
      method: 'POST',
      params: { action: 'add_note' },
      body: { lead_id, note },
    }),

  reassign: (lead_id: string, to_agent_id: string | null, reason?: string) =>
    callEdgeFunction('lead-management', {
      method: 'POST',
      params: { action: 'reassign' },
      body: { lead_id, to_agent_id, reason: reason || null },
    }),

  claim: (lead_id: string) =>
    callEdgeFunction('lead-management', {
      method: 'POST',
      params: { action: 'claim' },
      body: { lead_id },
    }),
};

// ─── User Profiles ─────────────────────────────────────────────
export const profileApi = {
  getOwn: () => callEdgeFunction('user-profiles'),

  getAgents: (page = 1, limit = 20) =>
    callEdgeFunction('user-profiles', { params: { action: 'agent', page: String(page), limit: String(limit) } }),

  getAgent: (id: string) =>
    callEdgeFunction('user-profiles', { params: { action: 'agent', id } }),

  getDevelopers: () =>
    callEdgeFunction('user-profiles', { params: { action: 'developer' } }),

  getAgencies: () =>
    callEdgeFunction('user-profiles', { params: { action: 'agency' } }),

  getAgency: (id: string) =>
    callEdgeFunction('user-profiles', { params: { action: 'agency', id } }),

  updateProfile: (data: any) =>
    callEdgeFunction('user-profiles', { method: 'PUT', body: data }),

  updateAgentProfile: (data: any) =>
    callEdgeFunction('user-profiles', { method: 'PUT', params: { action: 'agent' }, body: data }),

  updateDeveloperProfile: (data: any) =>
    callEdgeFunction('user-profiles', { method: 'PUT', params: { action: 'developer' }, body: data }),
};

// ─── Analytics ─────────────────────────────────────────────────
export const analyticsApi = {
  overview: () =>
    callEdgeFunction('analytics', { params: { action: 'overview' } }),

  propertyViews: (days = 30, propertyId?: string) =>
    callEdgeFunction('analytics', {
      params: {
        action: 'property_views',
        days: String(days),
        ...(propertyId ? { property_id: propertyId } : {}),
      },
    }),

  leads: () =>
    callEdgeFunction('analytics', { params: { action: 'leads' } }),

  siteStats: () =>
    callEdgeFunction('analytics', { params: { action: 'site_stats' } }),

  popular: (days = 7, limit = 10) =>
    callEdgeFunction('analytics', {
      params: { action: 'popular', days: String(days), limit: String(limit) },
    }),

  searchAnalytics: (days = 30) =>
    callEdgeFunction('analytics', { params: { action: 'search_analytics', days: String(days) } }),
};

// ─── Notifications ─────────────────────────────────────────────
export const notificationApi = {
  list: (page = 1, limit = 20) =>
    callEdgeFunction('notifications', {
      params: { page: String(page), limit: String(limit) },
    }),

  unreadCount: () =>
    callEdgeFunction('notifications', { params: { action: 'unread' } }),

  markRead: (notificationIds: string[]) =>
    callEdgeFunction('notifications', {
      method: 'PUT',
      params: { action: 'mark_read' },
      body: { notification_ids: notificationIds },
    }),

  markAllRead: () =>
    callEdgeFunction('notifications', {
      method: 'PUT',
      params: { action: 'mark_read' },
      body: { mark_all: true },
    }),
};
